import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import {
  Container,
  Row,
  Col,
  Nav,
  Tab,
  Card,
  Form,
  Button,
  InputGroup,
  ListGroup,
  Badge,
  Spinner,
  Alert,
  Navbar,
} from "react-bootstrap";
import {
  Plus,
  Trash2,
  Edit2,
  GripVertical,
  Save,
  X,
  Download,
  Rss,
  DollarSign,
  Newspaper,
  Upload,
  RefreshCw,
  Shield,
} from "lucide-react";

// Updated API URL to use config endpoint
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001/api";
const CONFIG_API_URL = `${API_BASE_URL}/config`;

const AdminDashboard = () => {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  return (
    <div className="min-vh-100 bg-light">
      {/* Top Navigation */}
      <Navbar bg="white" className="border-bottom shadow-sm">
        <Container fluid>
          <Navbar.Brand className="fw-bold">
            <Newspaper className="me-2" size={24} />
            Admin Dashboard
          </Navbar.Brand>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleLogout}
            className="d-flex align-items-center"
          >
            <LogOut size={16} className="me-2" />
            Logout
          </Button>
        </Container>
      </Navbar>

      <Container fluid className="py-4">
        <Tab.Container defaultActiveKey="rss">
          <Row>
            <Col xs={12}>
              <Card className="shadow-sm mb-4">
                <Card.Header className="bg-white">
                  <Nav variant="tabs" className="border-0">
                    <Nav.Item>
                      <Nav.Link
                        eventKey="rss"
                        className="d-flex align-items-center"
                      >
                        <Rss size={18} className="me-2" />
                        RSS Feed Management
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="blacklist"
                        className="d-flex align-items-center"
                      >
                        <Shield size={18} className="me-2" />
                        Content Filter
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="advertisement"
                        className="d-flex align-items-center"
                      >
                        <DollarSign size={18} className="me-2" />
                        Advertisement
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="banews"
                        className="d-flex align-items-center"
                      >
                        <Newspaper size={18} className="me-2" />
                        BA News
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Card.Header>
                <Card.Body className="p-0">
                  <Tab.Content>
                    <Tab.Pane eventKey="rss">
                      <RSSFeedManager />
                    </Tab.Pane>
                    <Tab.Pane eventKey="blacklist">
                      <BlacklistManager />
                    </Tab.Pane>
                    <Tab.Pane eventKey="advertisement">
                      <AdvertisementManager />
                    </Tab.Pane>
                    <Tab.Pane eventKey="banews">
                      <BANewsManager />
                    </Tab.Pane>
                  </Tab.Content>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab.Container>
      </Container>
    </div>
  );
};

const RSSFeedManager = () => {
  const [rssFeeds, setRssFeeds] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("bharat");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [newFeedUrl, setNewFeedUrl] = useState("");
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadFeeds();
  }, []);

  const loadFeeds = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${CONFIG_API_URL}/feeds`);
      const data = await response.json();

      if (data.success) {
        setRssFeeds(data.feeds);
        if (Object.keys(data.feeds).length > 0) {
          setSelectedCategory(Object.keys(data.feeds)[0]);
        }
        showMessage("Feeds loaded successfully", "success");
      } else {
        showMessage("Failed to load feeds", "danger");
      }
    } catch (error) {
      console.error("Error loading feeds:", error);
      showMessage(
        "Error connecting to server. Check if backend is running.",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  const saveFeeds = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${CONFIG_API_URL}/feeds`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ rssFeeds }),
      });

      const data = await response.json();

      if (data.success) {
        showMessage(
          "✅ Changes saved to rssFeeds.json successfully!",
          "success"
        );
      } else {
        showMessage("❌ Failed to save changes", "danger");
      }
    } catch (error) {
      console.error("Error saving feeds:", error);
      showMessage("❌ Error saving to server", "danger");
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text, variant) => {
    setMessage({ text, variant });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleAddFeed = () => {
    if (!newFeedUrl.trim()) return;
    setRssFeeds((prev) => ({
      ...prev,
      [selectedCategory]: [
        ...(prev[selectedCategory] || []),
        newFeedUrl.trim(),
      ],
    }));
    setNewFeedUrl("");
    showMessage('Feed added. Click "Save Changes" to update backend.', "info");
  };

  const handleDeleteFeed = (index) => {
    setRssFeeds((prev) => ({
      ...prev,
      [selectedCategory]: prev[selectedCategory].filter((_, i) => i !== index),
    }));
    showMessage(
      'Feed deleted. Click "Save Changes" to update backend.',
      "info"
    );
  };

  const handleStartEdit = (index, url) => {
    setEditingIndex(index);
    setEditValue(url);
  };

  const handleSaveEdit = (index) => {
    if (!editValue.trim()) return;
    setRssFeeds((prev) => ({
      ...prev,
      [selectedCategory]: prev[selectedCategory].map((url, i) =>
        i === index ? editValue.trim() : url
      ),
    }));
    setEditingIndex(null);
    setEditValue("");
    showMessage(
      'Feed updated. Click "Save Changes" to update backend.',
      "info"
    );
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue("");
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    const feeds = [...rssFeeds[selectedCategory]];
    const draggedItem = feeds[draggedIndex];
    feeds.splice(draggedIndex, 1);
    feeds.splice(index, 0, draggedItem);
    setRssFeeds((prev) => ({
      ...prev,
      [selectedCategory]: feeds,
    }));
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    showMessage(
      'Order changed. Click "Save Changes" to update backend.',
      "info"
    );
  };

  const handleAddCategory = () => {
    if (!newCategory.trim() || rssFeeds[newCategory.toLowerCase()]) return;
    const categoryKey = newCategory.toLowerCase().replace(/\s+/g, "_");
    setRssFeeds((prev) => ({
      ...prev,
      [categoryKey]: [],
    }));
    setSelectedCategory(categoryKey);
    setNewCategory("");
    setShowAddCategory(false);
    showMessage(
      'Category added. Click "Save Changes" to update backend.',
      "info"
    );
  };

  const handleDeleteCategory = () => {
    if (Object.keys(rssFeeds).length <= 1) {
      alert("Cannot delete the last category");
      return;
    }
    if (window.confirm(`Delete category "${selectedCategory}"?`)) {
      const newFeeds = { ...rssFeeds };
      delete newFeeds[selectedCategory];
      setRssFeeds(newFeeds);
      setSelectedCategory(Object.keys(newFeeds)[0]);
      showMessage(
        'Category deleted. Click "Save Changes" to update backend.',
        "info"
      );
    }
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(rssFeeds, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rssFeeds.json";
    a.click();
    URL.revokeObjectURL(url);
    showMessage("Configuration exported successfully", "success");
  };

  if (loading) {
    return (
      <div className="p-5 text-center">
        <Spinner animation="border" variant="primary" className="mb-3" />
        <h5 className="text-muted">Loading RSS feeds from backend...</h5>
        <p className="text-secondary small">
          Make sure your backend is running
        </p>
      </div>
    );
  }

  const categories = Object.keys(rssFeeds);
  const currentFeeds = rssFeeds[selectedCategory] || [];

  return (
    <Container fluid className="p-4">
      {/* Message Alert */}
      {message && (
        <Alert
          variant={message.variant}
          dismissible
          onClose={() => setMessage(null)}
          className="position-fixed top-0 end-0 m-3"
          style={{ zIndex: 1050, maxWidth: "400px" }}
        >
          {message.text}
        </Alert>
      )}

      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h3 className="fw-bold">RSS Feed Sources</h3>
          <p className="text-muted small mb-0">
            Manage RSS feeds - changes save directly to rssFeeds.json
          </p>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={loadFeeds}
            disabled={loading}
          >
            <RefreshCw
              size={18}
              className={loading ? "spinner-border spinner-border-sm" : ""}
            />
            <span className="ms-2">Refresh</span>
          </Button>
          <Button variant="secondary" onClick={handleExport}>
            <Download size={18} />
            <span className="ms-2">Export</span>
          </Button>
          <Button
            variant="primary"
            onClick={saveFeeds}
            disabled={saving}
            className="fw-semibold"
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <Upload size={18} />
                <span className="ms-2">Save Changes</span>
              </>
            )}
          </Button>
        </Col>
      </Row>

      <Row>
        {/* Categories Sidebar */}
        <Col md={3}>
          <Card className="shadow-sm">
            <Card.Body className="p-2">
              <ListGroup variant="flush">
                {categories.map((category) => (
                  <ListGroup.Item
                    key={category}
                    action
                    active={selectedCategory === category}
                    onClick={() => setSelectedCategory(category)}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <span className="text-capitalize">
                      {category.replace(/_/g, " ")}
                    </span>
                    <Badge
                      bg={selectedCategory === category ? "light" : "secondary"}
                      text={selectedCategory === category ? "dark" : "white"}
                    >
                      {rssFeeds[category]?.length || 0}
                    </Badge>
                  </ListGroup.Item>
                ))}
              </ListGroup>

              <div className="mt-3 px-2">
                {showAddCategory ? (
                  <Card className="border-primary">
                    <Card.Body className="p-2">
                      <Form.Control
                        size="sm"
                        type="text"
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        onKeyPress={(e) =>
                          e.key === "Enter" && handleAddCategory()
                        }
                        placeholder="Category name"
                        className="mb-2"
                      />
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={handleAddCategory}
                          className="flex-fill"
                        >
                          Add
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            setShowAddCategory(false);
                            setNewCategory("");
                          }}
                          className="flex-fill"
                        >
                          Cancel
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                ) : (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100 border-dashed"
                    onClick={() => setShowAddCategory(true)}
                  >
                    <Plus size={16} />
                    <span className="ms-2">New Category</span>
                  </Button>
                )}

                <Button
                  variant="outline-danger"
                  size="sm"
                  className="w-100 mt-2"
                  onClick={handleDeleteCategory}
                >
                  <Trash2 size={16} />
                  <span className="ms-2">Delete Category</span>
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Feed List */}
        <Col md={9}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-1 text-capitalize fw-bold">
                {selectedCategory.replace(/_/g, " ")}
              </h5>
              <p className="text-muted small mb-0">
                {currentFeeds.length} RSS feed
                {currentFeeds.length !== 1 ? "s" : ""} configured
              </p>
            </Card.Header>
            <Card.Body>
              {/* Add New Feed */}
              <Card className="bg-light mb-3 border-primary">
                <Card.Body className="p-3">
                  <Form.Label className="fw-semibold small">
                    Add New RSS Feed
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      value={newFeedUrl}
                      onChange={(e) => setNewFeedUrl(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleAddFeed()}
                      placeholder="https://example.com/rss/feed.xml"
                    />
                    <Button
                      variant="primary"
                      onClick={handleAddFeed}
                      disabled={!newFeedUrl.trim()}
                    >
                      <Plus size={18} />
                      <span className="ms-2">Add</span>
                    </Button>
                  </InputGroup>
                </Card.Body>
              </Card>

              {/* Feed Items */}
              {currentFeeds.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <Rss size={48} className="mb-3 opacity-50" />
                  <h5>No feeds added yet</h5>
                  <p className="small">Add your first RSS feed above</p>
                </div>
              ) : (
                <ListGroup>
                  {currentFeeds.map((feed, index) => (
                    <ListGroup.Item
                      key={index}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={draggedIndex === index ? "opacity-50" : ""}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div className="text-muted" style={{ cursor: "move" }}>
                          <GripVertical size={20} />
                        </div>

                        <div className="flex-fill">
                          {editingIndex === index ? (
                            <Form.Control
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyPress={(e) =>
                                e.key === "Enter" && handleSaveEdit(index)
                              }
                              autoFocus
                            />
                          ) : (
                            <div>
                              <code className="text-break small">{feed}</code>
                              <div className="text-muted small mt-1">
                                Position {index + 1} of {currentFeeds.length}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="d-flex gap-2">
                          {editingIndex === index ? (
                            <>
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => handleSaveEdit(index)}
                              >
                                <Save size={16} />
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleCancelEdit}
                              >
                                <X size={16} />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleStartEdit(index, feed)}
                              >
                                <Edit2 size={16} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteFeed(index)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const BlacklistManager = () => {
  const [blacklist, setBlacklist] = useState([]);
  const [newKeyword, setNewKeyword] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadBlacklist();
  }, []);

  const loadBlacklist = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${CONFIG_API_URL}/blacklist`);
      const data = await response.json();

      if (data.success) {
        setBlacklist(data.blacklist);
        showMessage("Blacklist loaded successfully", "success");
      } else {
        showMessage("Failed to load blacklist", "danger");
      }
    } catch (error) {
      console.error("Error loading blacklist:", error);
      showMessage("Error connecting to server", "danger");
    } finally {
      setLoading(false);
    }
  };

  const saveBlacklist = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${CONFIG_API_URL}/blacklist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ blacklist }),
      });

      const data = await response.json();

      if (data.success) {
        showMessage("✅ Blacklist saved successfully!", "success");
      } else {
        showMessage("❌ Failed to save blacklist", "danger");
      }
    } catch (error) {
      console.error("Error saving blacklist:", error);
      showMessage("❌ Error saving to server", "danger");
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text, variant) => {
    setMessage({ text, variant });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleAddKeyword = () => {
    if (!newKeyword.trim()) return;
    if (blacklist.includes(newKeyword.trim().toLowerCase())) {
      showMessage("Keyword already exists", "warning");
      return;
    }
    setBlacklist((prev) => [...prev, newKeyword.trim().toLowerCase()]);
    setNewKeyword("");
    showMessage(
      'Keyword added. Click "Save Changes" to update backend.',
      "info"
    );
  };

  const handleDeleteKeyword = (index) => {
    setBlacklist((prev) => prev.filter((_, i) => i !== index));
    showMessage(
      'Keyword deleted. Click "Save Changes" to update backend.',
      "info"
    );
  };

  const handleStartEdit = (index, keyword) => {
    setEditingIndex(index);
    setEditValue(keyword);
  };

  const handleSaveEdit = (index) => {
    if (!editValue.trim()) return;
    setBlacklist((prev) =>
      prev.map((kw, i) => (i === index ? editValue.trim().toLowerCase() : kw))
    );
    setEditingIndex(null);
    setEditValue("");
    showMessage(
      'Keyword updated. Click "Save Changes" to update backend.',
      "info"
    );
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue("");
  };

  if (loading) {
    return (
      <div className="p-5 text-center">
        <Spinner animation="border" variant="primary" className="mb-3" />
        <h5 className="text-muted">Loading blacklist from backend...</h5>
      </div>
    );
  }

  return (
    <Container fluid className="p-4">
      {message && (
        <Alert
          variant={message.variant}
          dismissible
          onClose={() => setMessage(null)}
          className="position-fixed top-0 end-0 m-3"
          style={{ zIndex: 1050, maxWidth: "400px" }}
        >
          {message.text}
        </Alert>
      )}

      <Row className="mb-4">
        <Col>
          <h3 className="fw-bold">Content Filter - Blacklist Keywords</h3>
          <p className="text-muted small mb-0">
            Articles containing these keywords will be filtered out
          </p>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={loadBlacklist}
            disabled={loading}
          >
            <RefreshCw size={18} />
            <span className="ms-2">Refresh</span>
          </Button>
          <Button
            variant="primary"
            onClick={saveBlacklist}
            disabled={saving}
            className="fw-semibold"
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <Upload size={18} />
                <span className="ms-2">Save Changes</span>
              </>
            )}
          </Button>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-1 fw-bold">Blacklisted Keywords</h5>
              <p className="text-muted small mb-0">
                {blacklist.length} keyword{blacklist.length !== 1 ? "s" : ""}{" "}
                configured
              </p>
            </Card.Header>
            <Card.Body>
              <Card className="bg-light mb-3 border-primary">
                <Card.Body className="p-3">
                  <Form.Label className="fw-semibold small">
                    Add New Keyword
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleAddKeyword()
                      }
                      placeholder="Enter keyword to filter"
                    />
                    <Button
                      variant="primary"
                      onClick={handleAddKeyword}
                      disabled={!newKeyword.trim()}
                    >
                      <Plus size={18} />
                      <span className="ms-2">Add</span>
                    </Button>
                  </InputGroup>
                </Card.Body>
              </Card>

              {blacklist.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <Shield size={48} className="mb-3 opacity-50" />
                  <h5>No keywords added yet</h5>
                  <p className="small">Add your first keyword above</p>
                </div>
              ) : (
                <ListGroup>
                  {blacklist.map((keyword, index) => (
                    <ListGroup.Item key={index}>
                      <div className="d-flex align-items-center gap-3">
                        <div className="flex-fill">
                          {editingIndex === index ? (
                            <Form.Control
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyPress={(e) =>
                                e.key === "Enter" && handleSaveEdit(index)
                              }
                              autoFocus
                            />
                          ) : (
                            <div>
                              <code className="text-break">{keyword}</code>
                              <div className="text-muted small mt-1">
                                Position {index + 1} of {blacklist.length}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="d-flex gap-2">
                          {editingIndex === index ? (
                            <>
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => handleSaveEdit(index)}
                              >
                                <Save size={16} />
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleCancelEdit}
                              >
                                <X size={16} />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleStartEdit(index, keyword)}
                              >
                                <Edit2 size={16} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteKeyword(index)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const AdvertisementManager = () => {
  return (
    <Container fluid className="p-5">
      <Row>
        <Col>
          <h3 className="fw-bold">Advertisement Management</h3>
          <p className="text-muted">
            Manage your advertisement placements and campaigns
          </p>
        </Col>
      </Row>
      <Card className="border-2 border-dashed text-center py-5">
        <Card.Body>
          <DollarSign size={48} className="text-muted mb-3" />
          <h5 className="text-muted">Advertisement management module</h5>
          <p className="text-secondary small">Coming soon...</p>
        </Card.Body>
      </Card>
    </Container>
  );
};

const BANewsManager = () => {
  return (
    <Container fluid className="p-5">
      <Row>
        <Col>
          <h3 className="fw-bold">BA News Management</h3>
          <p className="text-muted">
            Manage BA News portal content and articles
          </p>
        </Col>
      </Row>
      <Card className="border-2 border-dashed text-center py-5">
        <Card.Body>
          <Newspaper size={48} className="text-muted mb-3" />
          <h5 className="text-muted">BA News management module</h5>
          <p className="text-secondary small">Coming soon...</p>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AdminDashboard;
